import {
  CANONICAL_UNIT,
  CATEGORY_DEFAULTS,
  type Category,
  type Dimension,
  dec,
  fromCanonical,
  isDecimalString,
  sameSize,
  toCanonical,
  toFixedTrimmed,
  type UnitId,
  ZERO,
} from "@bl/domain";
import { useState } from "react";
import { usePacks } from "../api";
import { unitLabel } from "../format";

/** A pack as the API takes it: what it is called, and its size in whatever unit it was said. */
export type PackChoice = { name: string; size: { value: string; unit: UnitId } } | null;

/** The pack, or null while "Another size" is half filled in. */
export function usablePack(choice: PackChoice): PackChoice {
  if (!choice?.name.trim() || !isDecimalString(choice.size.value)) return null;
  return dec(choice.size.value).gt(ZERO) ? { ...choice, name: choice.name.trim() } : null;
}

/** An item's pack, or a lot's, in the form the picker holds. */
export function packChoiceOf(
  pack: { name: string; size: string } | null | undefined,
  dimension: Dimension,
): PackChoice {
  return pack
    ? { name: pack.name, size: { value: pack.size, unit: CANONICAL_UNIT[dimension] } }
    : null;
}

function canonicalOf(choice: PackChoice, dimension: Dimension): string | null {
  const usable = usablePack(choice);
  if (!usable) return null;
  try {
    return toCanonical(usable.size, dimension);
  } catch {
    return null;
  }
}

/**
 * Choosing a pack from the ones this system knows, rather than typing a name and a size.
 *
 * The list is the brewery's own names and then the built-in one, for this kind of item (Tyler,
 * 2026-09-23). "Another size" is still there for what nobody listed, and a size named that way is
 * remembered by the server, so it is on this list the next time.
 *
 * A pack within the list's tolerance of an option is that option: an item last received at 25 kg
 * reads as the 55 lb bag it is, and keeps its own 25 kg until someone picks something else.
 */
export function PackPicker({
  dimension,
  category,
  displayUnit,
  value,
  onChange,
  label = "Pack",
}: {
  dimension: Dimension;
  category: Category | null;
  displayUnit: UnitId;
  value: PackChoice;
  onChange: (next: PackChoice) => void;
  label?: string;
}) {
  const { data: options = [] } = usePacks(dimension, category);
  const canonical = canonicalOf(value, dimension);
  const matched =
    value && canonical
      ? options.findIndex((o) => o.name === value.name && sameSize(canonical, o.size))
      : -1;
  const [other, setOther] = useState(false);
  const custom = other || (value !== null && matched === -1);
  const [name, setName] = useState(value?.name ?? "");
  const [size, setSize] = useState(
    canonical ? toFixedTrimmed(fromCanonical(canonical, displayUnit), 3) : "",
  );

  const typed = (nextName: string, nextSize: string) => {
    setName(nextName);
    setSize(nextSize);
    onChange({ name: nextName, size: { value: nextSize, unit: displayUnit } });
  };

  return (
    <div className="stack pack-picker">
      <select
        className="select"
        aria-label={label}
        value={custom ? "other" : value === null ? "" : String(matched)}
        onChange={(e) => {
          const picked = e.target.value;
          if (picked === "") {
            setOther(false);
            onChange(null);
          } else if (picked === "other") {
            setOther(true);
            const noun = name || (category ? (CATEGORY_DEFAULTS[category].packName ?? "") : "");
            typed(noun, size);
          } else {
            const o = options[Number(picked)];
            if (!o) return;
            setOther(false);
            onChange({ name: o.name, size: { value: o.size, unit: CANONICAL_UNIT[dimension] } });
          }
        }}
      >
        <option value="">No pack</option>
        {options.map((o, i) => (
          <option key={`${o.name}-${o.size}`} value={String(i)}>
            {o.label}
          </option>
        ))}
        <option value="other">Another size…</option>
      </select>
      {custom ? (
        <div className="row pack-picker-other">
          <input
            className="input"
            aria-label="What one is called"
            placeholder="bag"
            value={name}
            onChange={(e) => typed(e.target.value, size)}
          />
          <input
            className="input num"
            inputMode="decimal"
            aria-label={`Size of one, in ${unitLabel(displayUnit)}`}
            placeholder={unitLabel(displayUnit)}
            value={size}
            onChange={(e) => typed(name, e.target.value)}
          />
          <span className="muted">{unitLabel(displayUnit)}</span>
        </div>
      ) : null}
    </div>
  );
}
